import React, { useEffect, useMemo, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ErrorProvider } from "./contexts/ErrorContext";
import TopModeBar from "./components/layout/TopModeBar";
import GunCmdPage from "./pages/GunCmdPage";
import GunSetupPage from "./pages/GunSetupPage";
import GunPositionPage from "./pages/GunPositionPage";
import FdcPage from "./pages/FdcPage";
import LoginPage from "./pages/LoginPage";

export const DarkModeContext = React.createContext({
  darkMode: true,
  setDarkMode: () => {},
});

function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to the user's default page
    const defaultPath =
      user?.role === "gun_commander" ? "/guncmd" : "/";
    return <Navigate to={defaultPath} replace />;
  }

  return children;
}

function AppRoutes() {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Determine default landing page based on role
  const defaultPath =
    user?.role === "gun_commander" ? "/guncmd" : "/";

  return (
    <>
      <TopModeBar />
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute allowedRoles={["admin", "fdc"]}>
              <FdcPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guncmd"
          element={
            <ProtectedRoute allowedRoles={["admin", "gun_commander"]}>
              <GunCmdPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guncmd/setup"
          element={
            <ProtectedRoute allowedRoles={["admin", "gun_commander"]}>
              <GunSetupPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guncmd/position"
          element={
            <ProtectedRoute allowedRoles={["admin", "gun_commander"]}>
              <GunPositionPage />
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={<Navigate to={defaultPath} replace />} />
        <Route path="*" element={<Navigate to={defaultPath} replace />} />
      </Routes>
    </>
  );
}

function AppRouter() {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("chacal-dark-mode");
    return saved ? saved === "true" : true;
  });

  useEffect(() => {
    localStorage.setItem("chacal-dark-mode", String(darkMode));
  }, [darkMode]);

  const theme = useMemo(
    () => {
      // Cleaner sans-serif stack (Inter primarily, system fallbacks).
      const sansFamily = [
        '"Inter"',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(', ');
      const monoFamily = [
        '"JetBrains Mono"',
        '"SF Mono"',
        'Menlo',
        'Consolas',
        'monospace',
      ].join(', ');

      return createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
          ...(darkMode
            ? {
                background: {
                  default: "#0f1115",
                  paper: "#161b22",
                },
                text: {
                  primary: "#e6edf3",
                  secondary: "#9da7b3",
                },
              }
            : {}),
        },
        typography: {
          fontFamily: sansFamily,
          fontWeightLight: 400,
          fontWeightRegular: 400,
          fontWeightMedium: 500,
          fontWeightBold: 600,
          // Tighten the default headings — they default to 500 in MUI.
          h1: { fontFamily: sansFamily, fontWeight: 600, letterSpacing: -0.5 },
          h2: { fontFamily: sansFamily, fontWeight: 600, letterSpacing: -0.4 },
          h3: { fontFamily: sansFamily, fontWeight: 600, letterSpacing: -0.3 },
          h4: { fontFamily: sansFamily, fontWeight: 600, letterSpacing: -0.2 },
          h5: { fontFamily: sansFamily, fontWeight: 600 },
          h6: { fontFamily: sansFamily, fontWeight: 600 },
          subtitle1: { fontFamily: sansFamily, fontWeight: 500 },
          subtitle2: { fontFamily: sansFamily, fontWeight: 500 },
          body1: { fontFamily: sansFamily, fontWeight: 400 },
          body2: { fontFamily: sansFamily, fontWeight: 400 },
          button: { fontFamily: sansFamily, fontWeight: 600, letterSpacing: 0.2 },
          caption: { fontFamily: sansFamily, fontWeight: 500 },
          overline: { fontFamily: sansFamily, fontWeight: 600, letterSpacing: 0.6 },
        },
        components: {
          // Globally cap the heaviest font-weights used inline throughout the app.
          // We keep "expressive" (700) for emphasis but skip the 800/900 the codebase
          // sprinkles around — Inter at 600/700 reads cleaner than Roboto at 800/900.
          MuiCssBaseline: {
            styleOverrides: {
              "html, body": {
                fontFamily: sansFamily,
                fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
              },
              // Numeric cells across the app — keep tabular figures so columns align.
              "[data-mono='true'], code, pre, kbd, samp": {
                fontFamily: monoFamily,
                fontVariantNumeric: "tabular-nums",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
          MuiAccordion: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: { fontWeight: 600, letterSpacing: 0.2 },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: { fontWeight: 500 },
              label: { fontWeight: 500 },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: { fontFamily: sansFamily },
            },
          },
        },
      });
    },
    [darkMode],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <ErrorProvider>
            <DarkModeContext.Provider value={{ darkMode, setDarkMode }}>
              <AppRoutes />
            </DarkModeContext.Provider>
          </ErrorProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default AppRouter;
